import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import api from "../../lib/api";
import SafeImage from "./SafeImage";

/**
 * RelatedArticles — combines curated `explicitSlugs` with auto-fetch by category.
 * Used at bottom of BlogPost and HubArticle.
 *
 * Props:
 *  - kind: "blog" | "hub"
 *  - currentSlug: string to exclude
 *  - category: string for auto-fetch fallback
 *  - explicitSlugs: string[] curated slugs (preferred)
 */
const RelatedArticles = ({ kind, currentSlug, category, explicitSlugs = [] }) => {
  const [items, setItems] = useState([]);
  const base = kind === "blog" ? "/blog" : "/hub";
  const linkBase = kind === "blog" ? "/blog" : "/content-hub";

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const curated = await Promise.all(
        (explicitSlugs || []).map((s) => api.get(`${base}/${s}`).then((r) => r.data).catch(() => null))
      );
      let list = curated.filter(Boolean).filter((a) => a.slug !== currentSlug);
      if (list.length < 3 && category) {
        const fallback = await api.get(base, { params: { category } }).then((r) => r.data).catch(() => []);
        for (const a of fallback) {
          if (list.length >= 3) break;
          if (a.slug === currentSlug) continue;
          if (list.find((x) => x.slug === a.slug)) continue;
          list.push(a);
        }
      }
      if (!cancelled) setItems(list.slice(0, 3));
    };
    load();
    return () => { cancelled = true; };
  }, [currentSlug, category, JSON.stringify(explicitSlugs), base]); // eslint-disable-line

  if (items.length === 0) return null;

  return (
    <section className="py-16 border-t border-border bg-[hsl(var(--secondary))]/40" data-testid={`related-${kind}-section`}>
      <div className="container-page">
        <div className="text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--accent))] mb-3">Keep reading</div>
        <h3 className="font-serif text-3xl">Related articles</h3>
        <div className="h-[2px] w-12 bg-[hsl(var(--accent))] mt-3" />
        <div className="mt-8 grid md:grid-cols-3 gap-8">
          {items.map((r) => (
            <Link key={r.slug} to={`${linkBase}/${r.slug}`} className="group block" data-testid={`related-${kind}-${r.slug}`}>
              <SafeImage src={r.cover_image} alt={r.title} label={r.title} className="rounded-xl border border-border aspect-[4/3] w-full" />
              <div className="mt-4 text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--accent))]">{r.category}</div>
              <h4 className="font-serif text-xl mt-1 group-hover:text-[hsl(var(--primary))] transition-colors">{r.title}</h4>
              <span className="mt-2 inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[hsl(var(--primary))]">
                Read <ArrowRight size={13} />
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default RelatedArticles;
